/**
 * Seeded PRNG (mulberry32) with a plain uint32 state so RunState stays a
 * serialisable value. Every function returns a new state; nothing mutates.
 */
export type RngState = number;

export interface RngStep<T> {
  readonly value: T;
  readonly state: RngState;
}

/** The size of the uint32 domain: a draw divided by it is a float in [0, 1). */
export const UINT32_RANGE = 4294967296;
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const MULBERRY_INCREMENT = 0x6d2b79f5;

/** Coerces any number to the uint32 domain mulberry32 expects. */
export function seedRng(seed: number): RngState {
  return Math.trunc(seed) >>> 0;
}

/** FNV-1a 32-bit. Used for string seeds such as the calendar-date daily seed. */
export function hashString(text: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, FNV_PRIME);
  }
  return hash >>> 0;
}

export function nextUint32(state: RngState): RngStep<number> {
  const nextState = (state + MULBERRY_INCREMENT) >>> 0;
  let t = nextState;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return { value: (t ^ (t >>> 14)) >>> 0, state: nextState };
}

/** Uniform float in [0, 1). */
export function nextFloat(state: RngState): RngStep<number> {
  const step = nextUint32(state);
  return { value: step.value / UINT32_RANGE, state: step.state };
}

/** Mixing constants of the keyed draw: odd, so every bit of the tick and the id reaches the state. */
const TICK_MIX = 0x9e3779b1;
const ID_MIX = 0x85ebca6b;

/**
 * A draw keyed by a tick and an id instead of taken from a stream (FL2's luck): the same seed,
 * tick and id give the same integer in [0, maxExclusive) on every machine, no other draw moves
 * it, and reading it spends nothing. Integer arithmetic only, mixed through mulberry32 twice.
 */
export function keyedInt(seed: number, tick: number, id: number, maxExclusive: number): number {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
    throw new RangeError(`keyedInt needs a positive integer bound, got ${maxExclusive}`);
  }
  const mixed = (seedRng(seed) ^ Math.imul(tick, TICK_MIX) ^ Math.imul(id, ID_MIX)) >>> 0;
  return nextUint32(nextUint32(mixed).value).value % maxExclusive;
}

/** Uniform integer in [0, maxExclusive). */
export function nextInt(state: RngState, maxExclusive: number): RngStep<number> {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
    throw new RangeError(`nextInt needs a positive integer bound, got ${maxExclusive}`);
  }
  const step = nextFloat(state);
  return { value: Math.floor(step.value * maxExclusive), state: step.state };
}
